import React, { useState, useMemo } from 'react';
import './ClassicPrint.css';
import VegIcon from '../../../components/VegIcon';
import { paginateMenu } from '../../../utils/menuPagination';
import { exportPtPagesToPdf } from '../../../utils/exportPtPagesToPdf';
import ExportPdfFab from '../../../components/ExportPdfFab';

const ClassicPrint = ({ data, isSingleMenu }) => {
    const restaurant = data?.restaurant || {};
    const rawMenus = data?.menus || [];

    const [activeMenuIndex, setActiveMenuIndex] = useState(0);

    // Determine which pages to display
    const visiblePages = useMemo(() => {
        if (isSingleMenu) {
            return rawMenus.flatMap(menu => paginateMenu(menu));
        }
        const activeMenu = rawMenus[activeMenuIndex];
        return activeMenu ? paginateMenu(activeMenu) : [];
    }, [isSingleMenu, rawMenus, activeMenuIndex]);

    const handleNext = () => {
        if (activeMenuIndex < rawMenus.length - 1) {
            setActiveMenuIndex(p => p + 1);
            window.scrollTo(0, 0);
        }
    };

    const handlePrev = () => {
        if (activeMenuIndex > 0) {
            setActiveMenuIndex(p => p - 1);
            window.scrollTo(0, 0);
        }
    };

    if (!data) return <div className="loading">Loading...</div>;

    return (
        <div className="cp-root">
            <ExportPdfFab onClick={() => exportPtPagesToPdf({ filename: `${restaurant.name || 'menu'}-classic-print.pdf` })} />

            {/* Navigation - Only show in Multi-Menu Mode (Preview) */}
            {!isSingleMenu && (
                <div className="tmpl-nav-container no-print" style={{ justifyContent: 'center' }}>
                    <div className="tmpl-nav-controls">
                        <button className="tmpl-nav-btn" onClick={handlePrev} disabled={activeMenuIndex === 0}>
                            ← Prev Menu
                        </button>
                        <div className="tmpl-page-indicator">
                            {rawMenus[activeMenuIndex]?.name || `Menu ${activeMenuIndex + 1}`}
                            <span style={{ fontSize: '0.8em', opacity: 0.7, marginLeft: '8px' }}>
                                ({activeMenuIndex + 1}/{rawMenus.length})
                            </span>
                        </div>
                        <button className="tmpl-nav-btn" onClick={handleNext} disabled={activeMenuIndex === rawMenus.length - 1}>
                            Next Menu →
                        </button>
                    </div>
                </div>
            )}

            <div className="cp-menu-container">
                {visiblePages.map((page, index) => {
                    const categories = (page.categories || []);

                    // Header logic:
                    // PDF: Only on very first page
                    // Preview: On first page of the active menu
                    const showHeader = isSingleMenu ? (index === 0) : (page.chunkIndex === 0);

                    // Menu Title Logic: Only show on first page of the menu chunk
                    const showMenuTitle = page.chunkIndex === 0;

                    return (
                        <article
                            key={`${page.id || page.name}-${index}`}
                            className="pt-page active"
                        >
                            {/* Restaurant header */}
                            {showHeader && (
                                <header className="cp-header">
                                    <h1 className="cp-name">{restaurant.name}</h1>
                                    {restaurant.address && <div className="cp-sub">{restaurant.address}</div>}
                                    {restaurant.phoneNumber && <div className="cp-sub">{restaurant.phoneNumber}</div>}
                                </header>
                            )}

                            {/* Menu content */}
                            <main className="cp-menu">
                                {showMenuTitle && (
                                    <h2 className="cp-menu-title">{page.name}</h2>
                                )}

                                {categories.map(category => (
                                    <section key={category.id} className="cp-category">
                                        <h3 className="cp-category-title">{category.name}</h3>
                                        <ul className="cp-items">
                                            {(category.items || []).map(item => (
                                                <li key={item.id} className="cp-item">
                                                    <div className="cp-item-row">
                                                        <div className="cp-item-name-group">
                                                            {(item.isVeg !== undefined && item.isVeg !== null) && (
                                                                <span className={`cp-veg-badge ${item.isVeg ? 'veg' : 'nonveg'}`}>
                                                                    <span className="cp-veg-mark"></span>
                                                                </span>
                                                            )}
                                                            <span className="cp-item-name">{item.name}</span>
                                                        </div>
                                                        <span className="cp-item-price">{item.price ? `₹${item.price.toFixed(2)}` : ''}</span>
                                                    </div>
                                                    {item.description && <div className="cp-item-desc">{item.description}</div>}
                                                </li>
                                            ))}
                                        </ul>
                                    </section>
                                ))}
                            </main>
                        </article>
                    );
                })}
            </div>
        </div>
    );
};

export default ClassicPrint;
